/* eslint-disable complexity */
import { useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  ROUTES,
  getOrganisationPredictUrl,
  joinRouteMatchUrl,
  workspacesSelector,
  consolidationsSelector,
  matchConsolidationDetailRoutePath,
  getConsolidationProductUrl,
  PREDICT_PRODUCT_SLUG
} from '@futrli/shared-services';
import { StoreState } from '@futrli/organisations/core/types';
import { getWorkspaceListingUrl } from '@futrli/predict-next/workspaces/utils/route-utils';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { Consolidation } from '@futrli-sdk/consolidations';

import { useSxpContext } from '../../core/sxp-context/sxp-context';

export interface SecondaryNavMenuItem {
  id: string;
  name: string;
  route: string;
  url: string;
  isActive?: boolean;
  submenu?: SecondaryNavMenuItem[];
}

export interface SecondaryNavRightItemChild {
  name: string;
  url: string;
}

export interface SecondaryNavRightItem {
  name: string;
  url?: string;
  children?: SecondaryNavRightItemChild[];
}

export const useAppSecondaryNavigation = () => {
  const intl = useIntl();
  const location = useLocation();
  const { displayGlobalNav, onlyExternalOrgs } = useSxpContext();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = matchConsolidationDetailRoutePath(location.pathname);

  const workspaces: WorkspacesListingItem[] = useSelector((store: StoreState) => {
    const { normalised } = store;
    return workspacesSelector(normalised) as WorkspacesListingItem[];
  });

  const consolidations: Consolidation[] = useSelector((store: StoreState) => {
    const { normalised } = store;
    const consolidationsMap = consolidationsSelector(normalised) || {};
    return Object.values(consolidationsMap);
  });

  const buildUrl = useCallback(
    (route: string) => {
      return joinRouteMatchUrl(
        '',
        isConsolidation
          ? getConsolidationProductUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG)
          : getOrganisationPredictUrl(workspaceId, organisationId),
        route
      );
    },
    [isConsolidation, workspaceId, organisationId]
  );

  const isRouteActive = useCallback(
    (url: string): boolean => {
      if (!url) {
        return false;
      }

      return location.pathname.startsWith(url);
    },
    [location.pathname]
  );

  const output = useMemo(() => {
    const menuItems: SecondaryNavMenuItem[] = [
      {
        id: 'insights',
        name: intl.formatMessage({ id: 'predict.overview', defaultMessage: 'Overview' }),
        route: ROUTES.HOME,
        url: buildUrl(ROUTES.HOME),
        isActive: isRouteActive(buildUrl(ROUTES.HOME))
      },
      {
        id: 'forecasts',
        name: intl.formatMessage({ id: 'reporting.forecasts', defaultMessage: 'Forecasts' }),
        route: ROUTES.FORECASTS,
        url: buildUrl(ROUTES.FORECASTS),
        isActive: isRouteActive(buildUrl(ROUTES.FORECASTS))
      },
      {
        id: 'reports',
        name: intl.formatMessage({ id: 'reporting.reports', defaultMessage: 'Reports' }),
        route: ROUTES.REPORTING,
        url: buildUrl(ROUTES.REPORTING),
        isActive: isRouteActive(buildUrl(ROUTES.REPORTING))
      },
      ...(isConsolidation
        ? []
        : [
            {
              id: 'dashboards',
              name: intl.formatMessage({ id: 'predict.dashboards', defaultMessage: 'Dashboards' }),
              route: ROUTES.DASHBOARDS,
              url: buildUrl(ROUTES.DASHBOARDS),
              isActive: isRouteActive(buildUrl(ROUTES.DASHBOARDS))
            },
            {
              id: 'settings',
              name: intl.formatMessage({ id: 'common.settings', defaultMessage: 'Settings' }),
              route: '',
              url: '',
              submenu: [
                {
                  id: 'chart-of-accounts',
                  name: intl.formatMessage({ id: 'predict.chart_of_accounts', defaultMessage: 'Chart of accounts' }),
                  route: ROUTES.CHART_OF_ACCOUNTS,
                  url: buildUrl(ROUTES.CHART_OF_ACCOUNTS),
                  isActive: isRouteActive(buildUrl(ROUTES.CHART_OF_ACCOUNTS))
                },
                {
                  id: 'non-financial-drivers',
                  name: intl.formatMessage({
                    id: 'predict.non_financial_drivers',
                    defaultMessage: 'Non-financial drivers'
                  }),
                  route: ROUTES.NON_FINANCIAL_DRIVERS,
                  url: buildUrl(ROUTES.NON_FINANCIAL_DRIVERS),
                  isActive: isRouteActive(buildUrl(ROUTES.NON_FINANCIAL_DRIVERS))
                }
              ]
            }
          ])
    ].filter(item => !isConsolidation || item.id !== 'insights');

    // Mark parent menu item as active if any child is active
    menuItems.forEach(item => {
      if (item.submenu && item.submenu.some(subItem => subItem.isActive)) {
        item.isActive = true;
      }
    });

    const rightSideItems: SecondaryNavRightItem[] = [];

    if (workspaces?.length) {
      const filteredWorkspaces = workspaces.filter(w => w.id !== workspaceId);
      if (displayGlobalNav && !onlyExternalOrgs) {
        filteredWorkspaces.unshift(workspaces.find(w => w.id === workspaceId) as WorkspacesListingItem);
      }
      if (filteredWorkspaces?.length) {
        rightSideItems.push({
          name: intl.formatMessage({ id: 'common.workspaces', defaultMessage: 'Workspaces' }),
          url: ROUTES.WORKSPACES_ROUTE
        });
      }
      // Only show consolidations if there are any
      if (consolidations && consolidations.length > 0) {
        rightSideItems.push({
          name: intl.formatMessage({ id: 'common.consolidations', defaultMessage: 'Consolidations' }),
          url: getWorkspaceListingUrl(workspaceId, 'consolidations')
        });
      }
    }

    return {
      menuItems,
      rightSideItems
    };
  }, [
    intl,
    buildUrl,
    isRouteActive,
    isConsolidation,
    workspaces,
    displayGlobalNav,
    onlyExternalOrgs,
    consolidations,
    workspaceId
  ]);

  return output;
};
